import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, localDateOf } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { dayLabel } from '../../lib/format';
import { useOrgClock } from '../../lib/useOrgClock';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { money } from '../money';
import { BillPartsSheet } from '../money/BillPartsSheet';
import { JobPriceEditor } from './JobPriceEditor';
import { WriteOffEditor } from './WriteOffEditor';
import { ChargeForm } from './ChargeForm';

/** The reconcile read, typed by the router (`partsBilling.forJob`). Carries no cost. */
export type JobParts = inferOutput<TRPCOptionsProxy<AppRouter>['partsBilling']['forJob']>;
type Part = JobParts['parts'][number];
type Charge = JobParts['charges'][number];
type Time = JobParts['time'][number];

/** Whether the job still takes write-offs, charges and undos: not once billed out, closed or canceled. */
const openJob = (data: JobParts) =>
  !['invoiced', 'closed', 'canceled'].includes(data.jobStatus ?? '');

/**
 * Inventory v2 Phase 1, ticket 05 (prototype verdict A): on a Fixed job, every part used, in
 * two groups. "Beyond the quote" comes first, with Add to invoice where the job's draft can take
 * it; "On the quote" sits quietly below. The server decides each part's state, whether it may
 * be added, and how many lines can't be matched; this card only draws that. A list, not a
 * table, so it reads at phone width with no sideways scroll.
 *
 * On a T&M job (ticket 06, amended 2026-10-06, P2/P3) each part says what was used, billed
 * across every invoice of the job, and left to bill; "Owed back" comes first, with its exact
 * amount and tax, then "Left to bill", "On the draft" and "Billed".
 */
export function PartsBillingCard({ jobId }: { jobId: string }) {
  const trpc = useTRPC();
  const parts = useQuery(trpc.partsBilling.forJob.queryOptions({ jobId }));
  const { role } = useSession();
  const data = parts.data;
  if (parts.isError) {
    return (
      <SectionCard title="Parts used">
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn't load how this job's parts are billed.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void parts.refetch()}
          >
            Try again
          </button>
        </p>
      </SectionCard>
    );
  }
  if (!data) return null;
  // A T&M job shows the card before anything is used: the office adds its charges here (12c).
  const empty =
    data.parts.length === 0 && data.charges.length === 0 && data.time.length === 0;
  if (
    empty &&
    (data.basis === 'fixed' || !can(role, 'invoice', 'update') || !openJob(data))
  ) {
    return null;
  }
  return <PartsBillingBody jobId={jobId} data={data} />;
}

/** The card itself, for a read that has answered. Exported so tests render it with data. */
export function PartsBillingBody({ jobId, data }: { jobId: string; data: JobParts }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const { tz } = useOrgClock();
  const mayAdd = can(role, 'invoice', 'update');
  const add = useMutation(
    trpc.partsBilling.addToInvoice.mutationOptions({
      onSettled: () => void qc.invalidateQueries(),
    }),
  );
  /**
   * The part whose Add to invoice was tapped: ONE add per tap, however fast the second comes.
   * `disabled` cannot stop a double tap: both taps land in the same render's handler before the
   * pending add re-renders the button, each minting its own key, and the second comes back
   * refused ("already on the invoice") after the first succeeded (TechVisitPage's
   * `stateTappedAt`, the same lock). Released only when the add is refused; one that lands
   * moves the part off "Beyond the quote", so its button is gone once the card is read again.
   */
  const addingRef = useRef<string | null>(null);
  const addPart = (jobCostId: string) => {
    if (addingRef.current === jobCostId) return;
    addingRef.current = jobCostId;
    add.mutate(
      { jobCostId, ...idem() },
      {
        onError: () => {
          if (addingRef.current === jobCostId) addingRef.current = null;
        },
      },
    );
  };

  if (data.basis !== 'fixed') {
    return (
      <TmBody
        jobId={jobId}
        data={data}
        tz={tz}
        mayBill={can(role, 'invoice', 'create')}
        mayPrice={mayAdd}
      />
    );
  }

  const owed = data.parts.filter((p) => p.state === 'credit_due');
  const beyond = data.parts.filter((p) => p.state === 'beyond_quote');
  const covered = data.parts.filter((p) => p.state === 'covered');
  const onInvoice = data.parts.filter(
    (p) => p.state === 'on_draft' || p.state === 'on_invoice',
  );
  const several = data.invoice === 'several';

  const row = (p: Part) => (
    <li key={p.jobCostId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{p.item.name}</p>
        <p className="text-xs text-muted-foreground">{usedBy(p, tz)}</p>
      </div>
      <span className="shrink-0 tabular-nums">
        {p.qty} × {money(p.unitPriceCents)}
      </span>
      <OwedBack part={p} />
      {p.state === 'beyond_quote' && p.beyondQty !== null && p.beyondQty < p.qty ? (
        <StatusBadge tone="brand">
          {round(p.qty - p.beyondQty)} on the quote · {p.beyondQty} beyond
        </StatusBadge>
      ) : null}
      {p.canAdd && mayAdd ? (
        <Button
          variant="ghost"
          aria-label={`Add ${p.item.name} to the invoice`}
          disabled={add.isPending}
          onClick={() => addPart(p.jobCostId)}
        >
          Add to invoice
        </Button>
      ) : p.state === 'beyond_quote' && p.beyondQty !== p.qty && draftOf(data) ? (
        // Adding all of it would bill the part already on the quote twice, and there is no
        // draft editor yet (build-map 053) to add part of it: say so, offer nothing false.
        <span className="text-xs text-muted-foreground">
          Partly on the quote, so it can't be added here
        </span>
      ) : null}
    </li>
  );

  const group = (
    label: string,
    tone: 'brand' | 'neutral' | 'info' | 'warn',
    list: Part[],
  ) =>
    list.length === 0 ? null : (
      <div>
        <p className="flex items-center gap-2 pt-1 text-xs font-semibold uppercase tracking-wide">
          {label} <StatusBadge tone={tone}>{list.length}</StatusBadge>
        </p>
        <ul className="divide-y divide-border text-sm">{list.map(row)}</ul>
      </div>
    );

  return (
    <SectionCard
      title={`Parts used (${data.parts.length})`}
      right={
        <span className="text-xs text-muted-foreground">
          {several
            ? 'Not matched'
            : [
                owed.length > 0 ? `${owed.length} owed back` : null,
                beyond.length > 0 ? `${beyond.length} beyond the quote` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'All on the quote'}
        </span>
      }
    >
      <div className="space-y-3">
        {add.error ? <Notice tone="danger">{add.error.message}</Notice> : null}
        {hintFor(data, beyond.length) ? (
          <Notice tone={several ? 'warn' : 'neutral'}>
            {hintFor(data, beyond.length)}
          </Notice>
        ) : null}
        {several ? null : (
          <>
            {group('Owed back', 'warn', owed)}
            {group('Beyond the quote', 'brand', beyond)}
            {group('On the invoice', 'info', onInvoice)}
            {group('On the quote', 'neutral', covered)}
            {owed.length > 0 ? <CreditsByHand /> : null}
          </>
        )}
        {data.unmatchedLines ? (
          <Notice tone="neutral">{unmatchedSentence(data.unmatchedLines)}</Notice>
        ) : null}
      </div>
    </SectionCard>
  );
}

/**
 * A T&M job's parts: owed back first (what the customer was billed beyond what stayed), then
 * what is left to bill, then what is on the draft, then what is billed. The server decides
 * every state and quantity; this draws them. Billing goes through one sheet (ticket 12a): "Bill
 * job" makes the draft, "Add to draft" adds to the open one, each part by as much as the office
 * picks.
 */
function TmBody({
  jobId,
  data,
  tz,
  mayBill,
  mayPrice,
}: {
  jobId: string;
  data: JobParts;
  tz: string | undefined;
  mayBill: boolean;
  mayPrice: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [billing, setBilling] = useState(false);
  // One row editor at a time: a part's price or write-off, hours written off, or the charge form.
  const [editing, setEditing] = useState<
    | { kind: 'price' | 'writeOff'; jobCostId: string }
    | { kind: 'writeOffTime'; timeEntryId: string }
    | { kind: 'charge' }
    | null
  >(null);
  const several = data.invoice === 'several';
  const open = openJob(data);
  const owed = data.parts.filter((p) => p.state === 'credit_due');
  const left = data.parts.filter((p) => p.state === 'waiting' || p.state === 'unbilled');
  const onDraft = data.parts.filter((p) => p.state === 'on_draft');
  const billed = data.parts.filter((p) => p.state === 'on_invoice');
  const chargesIn = (state: Charge['state']) =>
    data.charges.filter((c) => c.state === state);
  // Time (ticket 13), entry by entry, in the same groups; a running timer in its own.
  const timeIn = (...states: Time['state'][]) =>
    data.time.filter((t) => states.includes(t.state));
  const leftTime = timeIn('waiting', 'unbilled');
  const running = timeIn('running');
  const leftCount = leftToBill(data);
  const leftCents = data.counts?.leftCents ?? 0;
  // Hours with no rate on file have no price yet, so the money left can't count them.
  const unpriced = leftTime.some((t) => t.unitPriceCents === null);
  const owedCount = owed.length + timeIn('credit_due').length;
  const hint = tmHintFor(data, leftCount);
  const offerBill = offersBill(data, mayBill);
  const mayEdit = mayPrice && open && !data.jobCanceled;

  // Where focus goes once an editor closes, or a row's Remove or Undo is done: back to what opened
  // it, else to the card, never lost to the top of the page.
  const bodyId = `parts-charges-${jobId}`;
  const focusNext = useRef<string | null>(null);
  useEffect(() => {
    const id = focusNext.current;
    if (!id) return;
    focusNext.current = null;
    (document.getElementById(id) ?? document.getElementById(bodyId))?.focus();
  });
  const close = (triggerId: string) => {
    focusNext.current = triggerId;
    setEditing(null);
  };

  // Remove a charge, or undo a write-off: ONE write per tap, as Add to invoice (its `addingRef`).
  const busy = useRef<string | null>(null);
  const settled = (id: string) => () => {
    if (busy.current === id) busy.current = null;
  };
  const remove = useMutation(
    trpc.partsBilling.removeCharge.mutationOptions({
      onSuccess: () => {
        focusNext.current = bodyId;
      },
      onSettled: () => void qc.invalidateQueries(),
    }),
  );
  const undo = useMutation(
    trpc.partsBilling.undoWriteOff.mutationOptions({
      onSuccess: () => {
        focusNext.current = bodyId;
      },
      onSettled: () => void qc.invalidateQueries(),
    }),
  );
  const undoTime = useMutation(
    trpc.partsBilling.undoTimeWriteOff.mutationOptions({
      onSuccess: () => {
        focusNext.current = bodyId;
      },
      onSettled: () => void qc.invalidateQueries(),
    }),
  );
  const once = (id: string, fire: () => void) => {
    if (busy.current === id) return;
    busy.current = id;
    fire();
  };

  const partRow = (p: Part) => {
    const leftQty = p.split?.left ?? 0;
    // Priced only while none of it is on the draft (12b): the draft line keeps its price.
    const mayPriceIt = leftQty > 0 && (p.split?.onDraft ?? 0) === 0;
    const editingThis =
      editing && 'jobCostId' in editing && editing.jobCostId === p.jobCostId;
    return (
      <li key={p.jobCostId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{p.item.name}</p>
          <p className="text-xs text-muted-foreground">
            {usedBy(p, tz)} · <PriceText part={p} />
          </p>
        </div>
        {p.split ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {splitLabel(p.qty, p.split, p.writtenOff)}
          </span>
        ) : null}
        <OwedBack part={p} />
        {mayEdit && !editing && leftQty > 0 ? (
          <span className="flex gap-3">
            {/* A part's price for this job, ahead of billing (12b, decision 5). */}
            {mayPriceIt ? (
              <button
                type="button"
                id={`set-price-${p.jobCostId}`}
                className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
                aria-label={`Set the price of ${p.item.name} for this job`}
                onClick={() => setEditing({ kind: 'price', jobCostId: p.jobCostId })}
              >
                Set price
              </button>
            ) : null}
            {/* What won't be billed and can't go back to stock (12c, decision 3). */}
            <button
              type="button"
              id={`write-off-${p.jobCostId}-open`}
              className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
              aria-label={`Write off ${p.item.name}`}
              onClick={() => setEditing({ kind: 'writeOff', jobCostId: p.jobCostId })}
            >
              Write off
            </button>
          </span>
        ) : null}
        {editingThis && editing.kind === 'price' ? (
          <JobPriceEditor part={p} onDone={() => close(`set-price-${p.jobCostId}`)} />
        ) : null}
        {editingThis && editing.kind === 'writeOff' ? (
          <WriteOffEditor
            part={p}
            onDone={() => close(`write-off-${p.jobCostId}-open`)}
          />
        ) : null}
      </li>
    );
  };

  const timeRow = (t: Time) => {
    const name = timeName(t, tz);
    const editingThis =
      editing?.kind === 'writeOffTime' && editing.timeEntryId === t.timeEntryId;
    return (
      <li
        key={t.timeEntryId}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
      >
        <div className="min-w-0 flex-1">
          <p className="font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">
            {t.running ? 'Running' : `Time · ${duration(t.minutes)}`}
            {t.running || t.deleted || t.split.left === 0 ? null : (
              <>
                {' · '}
                <RateText time={t} labor={data.labor} />
              </>
            )}
          </p>
        </div>
        <span className="min-w-0 text-xs tabular-nums text-muted-foreground">
          {t.running
            ? 'Bills once the timer stops'
            : t.deleted
              ? 'Entry deleted'
              : hoursLabel(t)}
        </span>
        <OwedBack part={t} />
        {mayEdit && !editing && t.split.left > 0 ? (
          <button
            type="button"
            id={`write-off-time-${t.timeEntryId}-open`}
            className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
            aria-label={`Write off hours of ${name}`}
            onClick={() =>
              setEditing({ kind: 'writeOffTime', timeEntryId: t.timeEntryId })
            }
          >
            Write off
          </button>
        ) : null}
        {editingThis ? (
          <WriteOffEditor
            time={{ timeEntryId: t.timeEntryId, name, left: t.split.left }}
            onDone={() => close(`write-off-time-${t.timeEntryId}-open`)}
          />
        ) : null}
      </li>
    );
  };

  const chargeRow = (c: Charge) => (
    <li key={c.jobChargeId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{c.description}</p>
        <p className="text-xs text-muted-foreground">
          {addedBy(c, tz)} · {money(c.amountCents)}
          {c.taxable ? '' : ' · not taxed'}
        </p>
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">
        {c.state === 'left'
          ? 'Not billed yet'
          : c.state === 'on_draft'
            ? 'On the draft'
            : 'Billed'}
      </span>
      {c.state === 'left' && mayEdit && !editing ? (
        <button
          type="button"
          className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline disabled:opacity-40"
          aria-label={`Remove the charge ${c.description}`}
          disabled={remove.isPending}
          onClick={() =>
            once(c.jobChargeId, () =>
              remove.mutate(
                { jobChargeId: c.jobChargeId, ...idem() },
                { onSettled: settled(c.jobChargeId) },
              ),
            )
          }
        >
          Remove
        </button>
      ) : null}
    </li>
  );

  const group = (
    label: string,
    tone: 'brand' | 'neutral' | 'info' | 'warn',
    list: Part[],
    charges: Charge[] = [],
    times: Time[] = [],
  ) =>
    list.length + charges.length + times.length === 0 ? null : (
      <div>
        <p className="flex items-center gap-2 pt-1 text-xs font-semibold uppercase tracking-wide">
          {label}{' '}
          <StatusBadge tone={tone}>
            {list.length + charges.length + times.length}
          </StatusBadge>
        </p>
        <ul className="divide-y divide-border text-sm">
          {list.map(partRow)}
          {times.map(timeRow)}
          {charges.map(chargeRow)}
        </ul>
      </div>
    );

  const error = remove.error ?? undo.error ?? undoTime.error;
  const count = data.parts.length + data.charges.length + data.time.length;
  return (
    <SectionCard
      title={
        data.time.length > 0
          ? `Parts, time and charges (${count})`
          : `Parts and charges (${count})`
      }
      right={
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {several ? (
            'Not matched'
          ) : (
            <>
              {owedCount > 0 ? (
                <StatusBadge tone="warn">{owedCount} owed back</StatusBadge>
              ) : null}
              {leftCount > 0 ? (
                <StatusBadge tone={data.issued ? 'warn' : 'brand'}>
                  {money(leftCents)}
                  {unpriced ? ' + hours with no rate' : ''} left to bill
                </StatusBadge>
              ) : running.length > 0 ? (
                'A timer is running'
              ) : owedCount > 0 ? null : (
                'Everything is settled'
              )}
              {mayEdit ? (
                <Button
                  variant="ghost"
                  id={`add-charge-${jobId}`}
                  disabled={editing !== null}
                  onClick={() => setEditing({ kind: 'charge' })}
                >
                  Add a charge
                </Button>
              ) : null}
              {offerBill ? (
                <Button onClick={() => setBilling(true)}>
                  {draftOf(data) ? 'Add to draft' : 'Bill job'}
                </Button>
              ) : null}
            </>
          )}
        </span>
      }
    >
      <div id={bodyId} tabIndex={-1} className="space-y-3 focus:outline-none">
        {editing?.kind === 'charge' ? (
          <ChargeForm jobId={jobId} onDone={() => close(`add-charge-${jobId}`)} />
        ) : null}
        {error ? <Notice tone="danger">{error.message}</Notice> : null}
        {hint ? <Notice tone={several ? 'warn' : 'neutral'}>{hint}</Notice> : null}
        {several ? null : (
          <>
            {group('Owed back', 'warn', owed, [], timeIn('credit_due'))}
            {group(
              'Left to bill',
              data.issued ? 'warn' : 'brand',
              left,
              chargesIn('left'),
              leftTime,
            )}
            {group('Running', 'neutral', [], [], running)}
            {running.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                A running timer isn't billed yet. Its hours are left to bill once it
                stops.
              </p>
            ) : null}
            {group(
              'On the draft',
              'info',
              onDraft,
              chargesIn('on_draft'),
              timeIn('on_draft'),
            )}
            {group(
              'Billed',
              'neutral',
              billed,
              chargesIn('billed'),
              timeIn('on_invoice'),
            )}
            {data.writeOffs.length > 0 ? (
              <div>
                <p className="flex items-center gap-2 pt-1 text-xs font-semibold uppercase tracking-wide">
                  Written off{' '}
                  <StatusBadge tone="neutral">{data.writeOffs.length}</StatusBadge>
                </p>
                <ul className="divide-y divide-border text-sm">
                  {data.writeOffs.map((w) => (
                    <li
                      key={w.writeOffId}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">
                          {w.name} · {w.qty}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          “{w.reason}” · {writtenOffBy(w, tz)} · its cost stays on the job
                        </p>
                      </div>
                      {mayEdit ? (
                        <button
                          type="button"
                          className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline disabled:opacity-40"
                          aria-label={`Undo the write-off of ${w.qty} ${w.name}`}
                          disabled={undo.isPending}
                          onClick={() =>
                            once(w.writeOffId, () =>
                              undo.mutate(
                                { writeOffId: w.writeOffId, ...idem() },
                                { onSettled: settled(w.writeOffId) },
                              ),
                            )
                          }
                        >
                          Undo
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {data.timeWriteOffs.length > 0 ? (
              <div>
                <p className="flex items-center gap-2 pt-1 text-xs font-semibold uppercase tracking-wide">
                  Hours written off{' '}
                  <StatusBadge tone="neutral">{data.timeWriteOffs.length}</StatusBadge>
                </p>
                <ul className="divide-y divide-border text-sm">
                  {data.timeWriteOffs.map((w) => {
                    const of = data.time.find((t) => t.timeEntryId === w.timeEntryId);
                    const whose = of ? timeName(of, tz) : (w.who ?? 'Time');
                    return (
                      <li
                        key={w.writeOffId}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">
                            {whose} · {w.hours} h
                          </p>
                          <p className="text-xs text-muted-foreground">
                            “{w.reason}” · {whoAndDay(w.by, w.at, tz) || 'Written off'} ·
                            its cost stays on the job
                          </p>
                        </div>
                        {mayEdit ? (
                          <button
                            type="button"
                            className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline disabled:opacity-40"
                            aria-label={`Undo the write-off of ${w.hours} h of ${whose}`}
                            disabled={undoTime.isPending}
                            onClick={() =>
                              once(w.writeOffId, () =>
                                undoTime.mutate(
                                  { writeOffId: w.writeOffId, ...idem() },
                                  { onSettled: settled(w.writeOffId) },
                                ),
                              )
                            }
                          >
                            Undo
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {owed.length + timeIn('credit_due').length > 0 ? <CreditsByHand /> : null}
          </>
        )}
      </div>
      {billing ? (
        <BillPartsSheet jobId={jobId} onClose={() => setBilling(false)} />
      ) : null}
    </SectionCard>
  );
}

/** "4 used · 2 billed (1 on the draft) · 1 written off · 1 left". */
export function splitLabel(
  qty: number,
  split: NonNullable<Part['split']>,
  writtenOff = 0,
): string {
  const draft = split.onDraft > 0 ? ` (${split.onDraft} on the draft)` : '';
  const off = writtenOff > 0 ? ` · ${writtenOff} written off` : '';
  const left = split.left > 0 ? ` · ${split.left} left` : '';
  return `${qty} used · ${split.billed} billed${draft}${off}${left}`;
}

/** How many parts, charges and time entries are left to bill on a T&M job. */
export function leftToBill(data: JobParts): number {
  const leftState = (s: string) => s === 'waiting' || s === 'unbilled';
  return (
    data.parts.filter((p) => leftState(p.state)).length +
    data.charges.filter((c) => c.state === 'left').length +
    data.time.filter((t) => leftState(t.state)).length
  );
}

/**
 * Whether a T&M job offers Bill job, on the card and on the job page alike: to someone who may bill,
 * while something is left (a T&M invoice bills only what the job holds, 08), with one draft at most
 * and the job not canceled. Otherwise the sheet would offer nothing, or nothing it could send.
 */
export function offersBill(data: JobParts, mayBill: boolean): boolean {
  return (
    mayBill && leftToBill(data) > 0 && data.invoice !== 'several' && !data.jobCanceled
  );
}

/** Why what is left to bill isn't on a draft yet, when that is so; the two-drafts reason first. */
export function tmHintFor(data: JobParts, leftCount: number): string | null {
  if (data.invoice === 'several') {
    return 'This job has more than one draft invoice, so nothing can be added to either.';
  }
  if (leftCount === 0) return null;
  if (data.jobCanceled) return "This job is canceled, so what's left can't be invoiced.";
  // A part used while the draft is open, or taken off it, waits for the office (ruling B).
  if (data.invoice !== null)
    return "What isn't on the draft waits here until you add it.";
  return data.issued
    ? "What's left to bill goes on the job's next invoice."
    : 'No invoice yet. Bill job puts them on it, as much of each as you pick.';
}

/** "$38.00 each on this job (list $42.00)", or the price book's "$42.00 each" (12b). */
function PriceText({ part: p }: { part: Part }) {
  if (p.jobPriceCents !== null && p.jobPriceCents !== p.listPriceCents) {
    return (
      <>
        <span className="font-semibold text-foreground">
          {money(p.jobPriceCents)} each on this job
        </span>{' '}
        (list {money(p.listPriceCents)})
      </>
    );
  }
  return <>{money(p.unitPriceCents)} each</>;
}

/** "Owed back $20.35 ($18.80 + $1.55 tax)": a part (or hours) billed beyond what stayed. */
function OwedBack({ part: p }: { part: Pick<Part, 'creditDue'> }) {
  if (!p.creditDue) return null;
  return (
    <span className="text-xs text-[var(--sw-warn-text)]">
      Owed back {money(p.creditDue.totalCents)}{' '}
      <span className="text-muted-foreground">
        ({money(p.creditDue.subtotalCents)}
        {p.creditDue.taxCents ? ` + ${money(p.creditDue.taxCents)} tax` : ''})
      </span>
    </span>
  );
}

function CreditsByHand() {
  return (
    <p className="text-xs text-muted-foreground">
      Credits are settled by hand. Nothing here changes an issued invoice.
    </p>
  );
}

const draftOf = (data: JobParts) =>
  data.invoice !== null && data.invoice !== 'several' && data.invoice.status === 'draft';

const round = (n: number) => Number(n.toFixed(2));

/**
 * "Devon Bright · Tue, Oct 6": who used it and the shop's day, once the shop's zone is known.
 * A use that carried no visit (the office's job page, an older phone) says so, never a visit
 * guessed from the day (Inventory v2 Phase 1, F1).
 */
function usedBy(p: Part, tz: string | undefined): string {
  const who = whoAndDay(p.usedBy, p.usedAt, tz) || 'Used on this job';
  return p.visit ? who : `${who} · Visit not recorded`;
}

/** "Ana Ruiz · Tue, Oct 6", the shop's day once its zone is known. */
function whoAndDay(
  who: string | null,
  at: Date | string,
  tz: string | undefined,
): string {
  const day = tz ? dayLabel(localDateOf(new Date(at), tz), tz) : null;
  return [who, day].filter(Boolean).join(' · ');
}

/**
 * "Devon Ruiz · Wed, Oct 7 · 9:10 AM": whose time, and the shop's day and time it started, so
 * two entries of one person on one day read apart (on screen and to a screen reader).
 */
export function timeName(
  t: Pick<Time, 'who' | 'startedAt'>,
  tz: string | undefined,
): string {
  const day = whoAndDay(t.who ?? 'Time', t.startedAt, tz);
  if (!tz) return day;
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  }).format(new Date(t.startedAt));
  return `${day} · ${time}`;
}

/** "3 h 30 min". */
function duration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h > 0 ? `${h} h` : null, m > 0 || h === 0 ? `${m} min` : null]
    .filter(Boolean)
    .join(' ');
}

/** "3.5 h worked · 2 h billed (2 on the draft) · 1 h written off · 1.5 h left". */
export function hoursLabel(t: Pick<Time, 'hours' | 'split' | 'writtenOff'>): string {
  const draft = t.split.onDraft > 0 ? ` (${t.split.onDraft} on the draft)` : '';
  const off = t.writtenOff > 0 ? ` · ${t.writtenOff} h written off` : '';
  const left = t.split.left > 0 ? ` · ${t.split.left} h left` : '';
  return `${t.hours} h worked · ${t.split.billed} h billed${draft}${off}${left}`;
}

/** "$125.00/h, the job type's rate", or that no rate is set: the bill sheet takes one then. */
function RateText({ time: t, labor }: { time: Time; labor: JobParts['labor'] }) {
  if (t.unitPriceCents === null) {
    return (
      <span className="text-[var(--sw-danger-text)]">
        No hourly rate set: type one in the bill sheet
      </span>
    );
  }
  return (
    <>
      {money(t.unitPriceCents)}/h,{' '}
      {labor?.from === 'type' ? "the job type's rate" : "the shop's rate"}
    </>
  );
}

const addedBy = (c: Charge, tz: string | undefined) =>
  whoAndDay(c.addedBy, c.addedAt, tz) || 'Added to this job';

const writtenOffBy = (w: JobParts['writeOffs'][number], tz: string | undefined) =>
  whoAndDay(w.by, w.at, tz) || 'Written off';

/**
 * Why nothing beyond the quote can be added here, when that is so. Said only when some part
 * IS beyond the quote, and never as advice the product can't follow: a second invoice for
 * the job would leave it with two, and nothing would match.
 */
export function hintFor(data: JobParts, beyondCount: number): string | null {
  if (data.invoice === 'several') {
    return "This job has more than one live invoice, so its parts weren't matched to any of them.";
  }
  if (beyondCount === 0) return null;
  if (data.invoice === null) {
    return 'No invoice yet. Parts beyond the quote can be added once the draft exists.';
  }
  if (data.invoice.status !== 'draft') {
    const name = data.invoice.number ? `Invoice #${data.invoice.number}` : 'The invoice';
    return `${name} is no longer a draft, so nothing can be added to it.`;
  }
  return null;
}

/** "1 line on the quote isn't linked to a part, so no part can be matched to it." */
export function unmatchedSentence(u: NonNullable<JobParts['unmatchedLines']>): string {
  return u.count === 1
    ? `1 line on the ${u.on} isn't linked to a part, so no part can be matched to it.`
    : `${u.count} lines on the ${u.on} aren't linked to a part, so no part can be matched to them.`;
}
